import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, FileCheck, Search, Building, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export const ServicesSlider = ({ onOpenPopup }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const services = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Trademark Registration',
      description: 'Secure your brand identity with comprehensive federal trademark registration services.',
      link: '/services/trademark-registration',
    },
    {
      icon: <FileCheck className="h-8 w-8" />,
      title: 'Copyright Registration',
      description: 'Protect your creative works and original content with professional copyright registration.',
      link: '/services/copyright-registration',
    },
    {
      icon: <Search className="h-8 w-8" />,
      title: 'Trademark Search',
      description: 'Comprehensive search services to ensure your trademark is unique and registrable.',
      link: '/services/trademark-search',
    },
    {
      icon: <Building className="h-8 w-8" />,
      title: 'Company Formation',
      description: 'Complete business formation services to establish your company structure legally.',
      link: '/services/company-formation',
    },
    {
      icon: <Package className="h-8 w-8" />,
      title: 'Amazon Brand Registry',
      description: 'Protect your brand on Amazon with our specialized brand registry services.',
      link: '/services/amazon-brand-registry',
    },
    {
      icon: <ArrowRight className="h-8 w-8" />,
      title: 'Ready to Get Started?',
      description: 'Contact us today to protect your brand and secure your business future.',
      link: null,
      isCTA: true,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % services.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [services.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? services.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % services.length);
  };

  const getVisibleServices = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % services.length;
      visible.push({ ...services[index], key: index });
    }
    return visible;
  };

  const renderServiceCard = (service) => {
    if (service.isCTA) {
      return (
        <div
          onClick={onOpenPopup}
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-700 to-red-600 p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full cursor-pointer"
          data-testid="services-cta-box"
        >
          <div className="relative z-10 flex flex-col items-center justify-center text-center h-full min-h-[200px]">
            <div className="bg-white/20 rounded-xl w-16 h-16 flex items-center justify-center text-white mb-6">
              {service.icon}
            </div>
            <h3 className="font-heading font-bold text-2xl text-white mb-3">
              {service.title}
            </h3>
            <p className="text-white/90 leading-relaxed mb-4">
              {service.description}
            </p>
            <span className="inline-flex items-center text-white font-semibold bg-white/20 px-6 py-3 rounded-full hover:bg-white/30 transition-all">
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </span>
          </div>
        </div>
      );
    }

    return (
      <Link to={service.link}>
        <div className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-sm border border-slate-100 hover:shadow-xl hover:border-red-100 transition-all duration-300 h-full">
          <div className="relative z-10">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl w-16 h-16 flex items-center justify-center text-red-700 mb-6">
              {service.icon}
            </div>
            <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
              {service.title}
            </h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              {service.description}
            </p>
            <span className="inline-flex items-center text-red-700 font-semibold group-hover:gap-2 transition-all">
              Learn More
              <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="relative" data-testid="services-slider">
      {/* Desktop - 3 services */}
      <div className="hidden md:grid md:grid-cols-3 gap-8">
        {getVisibleServices().map((service, idx) => (
          <motion.div
            key={service.key}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
          >
            {renderServiceCard(service)}
          </motion.div>
        ))}
      </div>

      {/* Mobile - Single service */}
      <div className="md:hidden">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
        >
          {renderServiceCard(services[currentIndex])}
        </motion.div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-center gap-4 mt-8">
        <Button
          onClick={goToPrevious}
          variant="outline"
          size="icon"
          className="rounded-full h-12 w-12 border-slate-200 hover:bg-red-50 hover:border-red-200"
          data-testid="services-prev-button"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Button>
        <Button
          onClick={goToNext}
          variant="outline"
          size="icon"
          className="rounded-full h-12 w-12 border-slate-200 hover:bg-red-50 hover:border-red-200"
          data-testid="services-next-button"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4">
        {services.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-8 bg-red-600' : 'w-2 bg-slate-300'
            }`}
            data-testid={`services-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};
